/**
 * LLM Studio — clean chat surface (redesigned per Rohit spec 2026-05-23).
 *
 * BACKEND CONTRACT (do NOT change — docs live in ``routes/llm_studio.py``):
 *   GET  /api/llm/models         → catalogue for the model dropdown
 *   POST /api/llm/chat           → SSE: event: meta|token|done|error
 *   POST /api/llm/uploads        → multipart file attachments
 *   POST /api/llm/images         → gpt-image-2 job kickoff + polling GET
 *   *    /api/llm/conversations  → CRUD for the sidebar
 *
 * DESIGN TOKENS (light variant — ChatGPT-style day mode):
 *   bg-main:      #FFFFFF   chat area
 *   bg-sidebar:   #F9F9F9   sidebar
 *   bg-user:      #E9E9E9   user message bubble
 *   bg-hover:     #ECECEC   sidebar row + button hover
 *   text-primary: #0D0D0D   body text
 *   text-secondary:#5D5D5D  icons + model picker
 *   text-tertiary:#8E8E8E   timestamps + disclaimer
 *   border:       #E5E5E5   dividers + composer
 *   send-active:  #0D0D0D   active send button (dark pill, white icon)
 *   send-idle:    #D9D9D9   idle send button
 *   accent (pinned tool chip ONLY): fg #2B5FD9 on bg rgba(43,95,217,0.10)
 *
 * NO gradients, NO purple/violet, NO glows. Iconography via lucide-react
 * (1.5–2px stroke matches spec). Chat column is capped at 768px, centered.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Search, MessageSquare, ArrowUp, Paperclip, Sparkles, Upload,
  Image as ImageIcon, ChevronDown, Trash2, Square, Copy, Check, Globe,
  ArrowLeft, PanelLeft, Mic, X, PenSquare,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ── Design tokens (kept out of JSX so hover states stay declarative) ──
const T = {
  bg:        '#FFFFFF',
  sidebar:   '#F9F9F9',
  userBubble:'#F4F4F4',
  hover:     '#ECECEC',
  primary:   '#0D0D0D',
  secondary: '#5D5D5D',
  tertiary:  '#8E8E8E',
  border:    '#E5E5E5',
  sendIdle:  '#D9D9D9',
  sendActive:'#0D0D0D',
  accentFg:  '#2B5FD9',
  accentBg:  'rgba(43,95,217,0.10)',
  accentBd:  'rgba(43,95,217,0.25)',
  errFg:     '#DC2626',
  errBg:     'rgba(220,38,38,0.08)',
  errBd:     'rgba(220,38,38,0.25)',
};

// ── Group conversations into the spec's five time buckets ──
const bucketize = (items) => {
  const now = Date.now();
  const day = 86400_000;
  const buckets = { Today: [], Yesterday: [], 'Previous 7 days': [], 'Previous 30 days': [], Older: [] };
  for (const c of items) {
    const ts = c.updated_at ? new Date(c.updated_at).getTime() : now;
    const age = now - ts;
    if (age < day) buckets.Today.push(c);
    else if (age < 2 * day) buckets.Yesterday.push(c);
    else if (age < 7 * day) buckets['Previous 7 days'].push(c);
    else if (age < 30 * day) buckets['Previous 30 days'].push(c);
    else buckets.Older.push(c);
  }
  return buckets;
};

const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86_400) return `${Math.floor(secs / 3600)}h`;
  return new Date(iso).toLocaleDateString();
};

export default function LLMStudioPage() {
  const { convId } = useParams();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState({ openai: [], anthropic: [] });
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-5.5');
  const [modelOpen, setModelOpen] = useState(false);

  const [conversations, setConversations] = useState([]);
  const [convFilter, setConvFilter] = useState('');
  const [activeConv, setActiveConv] = useState(null);
  const activeId = activeConv?.id || convId || null;

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [plusOpen, setPlusOpen] = useState(false);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const taRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // 2026-05-24 (Rohit P2) — mobile edge-swipe drawer state.
  // `isMobile` gates the whole gesture layer (< 768px viewport).
  // `dragX` (0 → 260) tracks the finger 1:1 while a horizontal swipe is
  // active; on release we snap open or closed via a CSS transition.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const on = (e) => setIsMobile(e.matches);
    mq.addEventListener?.('change', on);
    // On desktop the sidebar defaults to open; on mobile default to closed
    // (mimics the ChatGPT layout — content-first, drawer summoned by swipe).
    setSidebarOpen(!mq.matches);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  const dragRef = useRef({
    active: false,        // gesture engaged (locked to horizontal)
    startX: 0, startY: 0, startedInEdge: false, startedOnOpen: false,
    lastX: 0, lastT: 0,   // for velocity
    dx: 0,                // current pointer delta
  });
  const [dragX, setDragX] = useState(null); // null = not dragging (use CSS)
  const SIDEBAR_W = 260;
  const EDGE_ZONE = 40;   // px from left where a swipe-open starts (>20 to dodge iOS system edge)
  const IOS_INSET = 20;   // reserved for the system back gesture
  const COMMIT_RATIO = 0.4;      // >40% drag commits
  const COMMIT_VELOCITY = 0.3;   // px/ms flick threshold

  const _isInScrollableRow = (el) => {
    // Ignore the gesture if it starts inside an element that has horizontal
    // room to scroll and isn't already at its edge — avoids hijacking code
    // blocks, image carousels, tables (spec §Guards).
    let node = el;
    let hops = 0;
    while (node && hops < 8) {
      try {
        const s = node.scrollWidth || 0;
        const cw = node.clientWidth || 0;
        if (s > cw + 4) {
          const l = node.scrollLeft || 0;
          const atLeftEdge = l <= 1;
          const atRightEdge = l >= (s - cw - 1);
          if (!atLeftEdge && !atRightEdge) return true;
        }
      } catch { /* no-op */ }
      node = node.parentElement;
      hops += 1;
    }
    return false;
  };

  const onPointerDown = (e) => {
    if (!isMobile) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const x = e.clientX, y = e.clientY;
    const inEdge = x >= IOS_INSET && x <= EDGE_ZONE;
    const canStart = (!sidebarOpen && inEdge) || (sidebarOpen);
    if (!canStart) return;
    if (_isInScrollableRow(e.target)) return;
    dragRef.current = {
      active: false, // will lock in pointermove once dominant axis is known
      startX: x, startY: y,
      startedInEdge: inEdge, startedOnOpen: sidebarOpen,
      lastX: x, lastT: performance.now(), dx: 0,
    };
  };

  const onPointerMove = (e) => {
    if (!isMobile) return;
    const d = dragRef.current;
    if (!d.startX) return;
    const x = e.clientX, y = e.clientY;
    const dx = x - d.startX;
    const dy = y - d.startY;
    // Lock the axis after ~10px of movement
    if (!d.active) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      if (Math.abs(dx) <= Math.abs(dy)) {
        // Vertical intent — release and let the browser scroll
        d.startX = 0; return;
      }
      // Sign check — swipe-open requires rightward, swipe-close requires leftward
      if (!d.startedOnOpen && dx <= 0) { d.startX = 0; return; }
      if (d.startedOnOpen && dx >= 0) { d.startX = 0; return; }
      d.active = true;
    }
    d.lastX = x; d.lastT = performance.now(); d.dx = dx;
    // Compute drawer position (0…SIDEBAR_W)
    let pos;
    if (d.startedOnOpen) {
      pos = Math.max(0, Math.min(SIDEBAR_W, SIDEBAR_W + dx));
    } else {
      pos = Math.max(0, Math.min(SIDEBAR_W, dx));
    }
    setDragX(pos);
    // Prevent vertical page scroll from stealing the gesture mid-drag
    if (e.cancelable) e.preventDefault();
  };

  const onPointerUpOrCancel = () => {
    if (!isMobile) return;
    const d = dragRef.current;
    if (!d.active) { dragRef.current = { active: false, startX: 0, startY: 0, dx: 0, lastX: 0, lastT: 0, startedInEdge: false, startedOnOpen: false }; return; }
    const now = performance.now();
    const dt = Math.max(1, now - d.lastT);
    const v = d.dx / dt; // px/ms, signed
    const pos = dragX == null ? (d.startedOnOpen ? SIDEBAR_W : 0) : dragX;
    const ratio = pos / SIDEBAR_W;
    let commitOpen;
    if (d.startedOnOpen) {
      // We were open, dragging left. Commit closed if enough progress or flick left.
      commitOpen = !(ratio < (1 - COMMIT_RATIO) || v < -COMMIT_VELOCITY);
    } else {
      // We were closed, dragging right.
      commitOpen = (ratio > COMMIT_RATIO || v > COMMIT_VELOCITY);
    }
    setSidebarOpen(commitOpen);
    setDragX(null);
    dragRef.current = { active: false, startX: 0, startY: 0, dx: 0, lastX: 0, lastT: 0, startedInEdge: false, startedOnOpen: false };
  };


  // Image + web toggles (preserved from previous shell)
  const [imgMode, setImgMode] = useState(false);
  const [imgCount, setImgCount] = useState(3);
  const [imgSize, setImgSize] = useState('1536x1024');
  const [useWebSearch, setUseWebSearch] = useState(false);
  // Full image-prompt modal — reachable from + menu for operators who
  // want to pick count/aspect before firing gpt-image-2.
  const [imagePromptOpen, setImagePromptOpen] = useState(false);
  const [imgPrompt, setImgPrompt] = useState('');

  const token = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/llm/models`, { headers: authHeaders() });
        if (r.ok) {
          const d = await r.json();
          setCatalog(d);
          // Only apply the catalog default when we're on a fresh chat
          // (no convId in URL). Otherwise the convId useEffect owns state
          // and this would race with it → provider/model mismatch.
          if (!convId) {
            // Prefer OpenAI default (GPT-5.5) as the global default for
            // fresh chats. Fall back to Anthropic default if OpenAI has none.
            const defOpen = (d.openai || []).find(m => m.default);
            const defAnth = (d.anthropic || []).find(m => m.default);
            if (defOpen) { setProvider('openai'); setModel(defOpen.id); }
            else if (defAnth) { setProvider('anthropic'); setModel(defAnth.id); }
          }
        }
      } catch (e) { /* no-op */ }
    })();
    refreshConvs('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!convId) { setActiveConv(null); setMessages([]); return; }
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/llm/conversations/${convId}`, { headers: authHeaders() });
        if (r.ok) {
          const d = await r.json();
          setActiveConv(d);
          setMessages(d.messages || []);
          // Atomic provider+model update — infer whichever is missing from
          // the other so we never end up with a mismatched pair (e.g.
          // provider='openai' + model='claude-opus-4-8' → OpenAI 404).
          if (d.provider || d.model) {
            const inferProvider = (m) => (m || '').toLowerCase().startsWith('claude') ? 'anthropic' : 'openai';
            const nextProvider = d.provider || inferProvider(d.model);
            const nextModel = d.model || (nextProvider === 'anthropic' ? 'claude-opus-4-8' : 'gpt-5.2');
            // If provider/model are inconsistent, trust the model name.
            const finalProvider = (nextProvider === 'anthropic' && !String(nextModel).toLowerCase().startsWith('claude'))
              || (nextProvider === 'openai' && String(nextModel).toLowerCase().startsWith('claude'))
                ? inferProvider(nextModel)
                : nextProvider;
            setProvider(finalProvider);
            setModel(nextModel);
          }
        }
      } catch (e) { /* no-op */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, streaming]);

  const autoGrow = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  };

  const refreshConvs = useCallback(async (q) => {
    try {
      const url = new URL(`${API_BASE}/api/llm/conversations`);
      if (q) url.searchParams.set('q', q);
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setConversations(d.items || []);
      }
    } catch (e) { /* no-op */ }
  }, []);

  const startNewChat = () => {
    setActiveConv(null); setMessages([]); setInput(''); setAttachments([]); setImgMode(false); setUseWebSearch(false);
    navigate('/llm-studio');
    setTimeout(() => taRef.current?.focus(), 20);
  };

  // ── Send (chat SSE) or image-mode fork (identical backend contract) ──
  const send = async (overrideText) => {
    if (streaming) return;
    const text = (overrideText ?? input).trim();
    if (!text && attachments.length === 0) return;

    if (imgMode) {
      const tmpUserId = `u-${Date.now()}`;
      const tmpCardId = `img-${Date.now()}`;
      setMessages(m => [
        ...m,
        { id: tmpUserId, role: 'user', content: `Generate images: ${text}`, ts: new Date().toISOString() },
        { id: tmpCardId, role: 'assistant', type: 'image_grid', prompt: text, count: imgCount, size: imgSize, status: 'running', slides: [], ts: new Date().toISOString() },
      ]);
      setInput('');
      if (taRef.current) taRef.current.style.height = 'auto';
      const imgRefs = attachments.filter(a => (a.mime || '').startsWith('image/')).map(a => ({ url: a.url, mime: a.mime }));
      setAttachments([]);
      try {
        const r = await fetch(`${API_BASE}/api/llm/images`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: text, count: imgCount, size: imgSize, quality: 'medium',
            conversation_id: activeId || undefined,
            reference_files: imgRefs.length ? imgRefs : undefined,
          }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const respJson = await r.json();
        const { job_id, user_message_id, assistant_message_id, conversation_id: newConvId, conversation_created } = respJson;
        const cardId = assistant_message_id || tmpCardId;
        if (assistant_message_id || user_message_id) {
          setMessages(m => m.map(x => {
            if (x.id === tmpUserId && user_message_id) return { ...x, id: user_message_id };
            if (x.id === tmpCardId && assistant_message_id) return { ...x, id: assistant_message_id };
            return x;
          }));
        }
        if (conversation_created && newConvId && !activeId) {
          setActiveConv(c => c || { id: newConvId, provider, model, title: '' });
          window.history.replaceState({}, '', `/llm-studio/${newConvId}`);
          refreshConvs(convFilter);
        }
        for (let i = 0; i < 100; i++) {
          await new Promise(res => setTimeout(res, 3000));
          const pr = await fetch(`${API_BASE}/api/llm/images/${job_id}`, { headers: authHeaders() });
          if (!pr.ok) continue;
          const doc = await pr.json();
          setMessages(m => m.map(x => x.id === cardId ? { ...x, status: doc.status, slides: doc.slides || [], errors: doc.errors || [] } : x));
          if (doc.status === 'done' || doc.status === 'error') break;
        }
      } catch (e) {
        setMessages(m => m.map(x => x.id === tmpCardId ? { ...x, status: 'error', error: e.message || 'Request failed' } : x));
      }
      return;
    }

    // ── Text/chat SSE ──
    const userTurn = {
      id: `u-${Date.now()}`, role: 'user',
      content: text || '(attachments)',
      attachments: attachments.map(a => ({ file_id: a.file_id, url: a.url, name: a.name, mime: a.mime })),
      ts: new Date().toISOString(),
    };
    const nextMessages = [...messages, userTurn];
    setMessages(nextMessages);
    setInput('');
    if (taRef.current) taRef.current.style.height = 'auto';
    setAttachments([]);

    const asstId = `a-${Date.now()}`;
    setMessages(m => [...m, { id: asstId, role: 'assistant', content: '', ts: new Date().toISOString(), streaming: true }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const r = await fetch(`${API_BASE}/api/llm/chat`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          conversation_id: activeId, provider, model,
          messages: nextMessages.map(mm => ({ role: mm.role, content: mm.content, attachments: mm.attachments || [] })),
          temperature: 0.7, max_tokens: 2048, use_web_search: useWebSearch,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);

      const reader = r.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';
      let bindConvId = activeId;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const part of parts) {
          const lines = part.split('\n');
          let evt = 'message';
          let data = '';
          for (const l of lines) {
            if (l.startsWith('event:')) evt = l.slice(6).trim();
            else if (l.startsWith('data:')) data = l.slice(5).trim();
          }
          if (!data) continue;
          try {
            const payload = JSON.parse(data);
            if (evt === 'meta' && payload.conversation_id) {
              bindConvId = payload.conversation_id;
              if (!activeId) {
                setActiveConv(c => c || { id: bindConvId, provider, model, title: '' });
                window.history.replaceState({}, '', `/llm-studio/${bindConvId}`);
              }
            } else if (evt === 'token' && payload.delta) {
              setMessages(m => m.map(x => x.id === asstId ? { ...x, content: (x.content || '') + payload.delta } : x));
            } else if (evt === 'tool_start' && payload.tool === 'generate_image') {
              // 2026-05-23 (Rohit E1) — shimmer image card while the tool
              // runs. Attach onto the assistant message as a
              // `pending_images` array keyed by tool_call_id.
              setMessages(m => m.map(x => {
                if (x.id !== asstId) return x;
                const pending = [...(x.pending_images || [])];
                if (!pending.find(p => p.tool_call_id === payload.tool_call_id)) {
                  pending.push({ tool_call_id: payload.tool_call_id, prompt: payload.prompt || '', reference_image_id: payload.reference_image_id || null });
                }
                return { ...x, pending_images: pending };
              }));
            } else if (evt === 'tool_result' && payload.tool === 'generate_image') {
              const res = payload.result || {};
              setMessages(m => m.map(x => {
                if (x.id !== asstId) return x;
                const pending = (x.pending_images || []).filter(p => p.tool_call_id !== payload.tool_call_id);
                const images = [...(x.images || [])];
                if (res.url && !res.error) {
                  images.push({ tool_call_id: payload.tool_call_id, image_id: res.image_id, url: res.url, mime: res.mime, size: res.size });
                }
                const errs = [...(x.image_errors || [])];
                if (res.error) errs.push({ tool_call_id: payload.tool_call_id, message: res.error });
                return { ...x, pending_images: pending, images, image_errors: errs };
              }));
            } else if (evt === 'error') {
              setMessages(m => m.map(x => x.id === asstId ? { ...x, error: payload.message || 'Stream error', streaming: false } : x));
            } else if (evt === 'done') {
              setMessages(m => m.map(x => x.id === asstId ? { ...x, streaming: false, usage: payload.usage } : x));
            }
          } catch (e) { /* ignore parse errors */ }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages(m => m.map(x => x.id === asstId ? { ...x, error: e.message || 'Request failed', streaming: false } : x));
      } else {
        setMessages(m => m.map(x => x.id === asstId ? { ...x, streaming: false, stopped: true } : x));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      refreshConvs(convFilter);
    }
  };

  const stopStream = () => { if (abortRef.current) abortRef.current.abort(); };

  // ── Uploads (from + menu) ──
  const fileInputRef = useRef(null);
  const pendingUploads = useRef(new Map()); // tempId → blobUrl (for revokeObjectURL cleanup)
  const onUploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    // 2026-07-09 — Ensure we have a conversation id BEFORE the upload so
    // `/llm/uploads` can register the image in `chat_images` for this
    // conversation. Without a convId the backend used to skip registration
    // → the LLM saw an empty manifest → generate_image fired with no
    // reference_image_id → completely wrong image. (Backend has a safety
    // net now too, but eager creation keeps the manifest in-sync from
    // the very first upload.)
    let convForUpload = activeId;
    if (!convForUpload) {
      try {
        const r = await fetch(`${API_BASE}/api/llm/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ title: 'New chat', provider, model }),
        });
        if (r.ok) {
          const d = await r.json();
          convForUpload = d.id;
          setActiveConv(d);
          // Push to URL so a page refresh keeps the same convId.
          try { navigate(`/llm-studio/${d.id}`, { replace: true }); } catch (e) { /* no-op */ }
        }
      } catch (e) { /* fall through — backend safety net will backfill */ }
    }

    // ── OPTIMISTIC CHIPS — render instantly like ChatGPT does ─────────
    // Create a local blob-URL preview for each file so the chip appears
    // the moment the user picks it. When the server responds, we swap
    // the temp entries out for the real {file_id, url, mime, name} rows.
    const fileArr = Array.from(files);
    const optimistic = fileArr.map((f) => {
      const tempId = `temp-${Math.random().toString(36).slice(2, 10)}`;
      const isImg = (f.type || '').startsWith('image/');
      const blobUrl = isImg ? URL.createObjectURL(f) : '';
      if (blobUrl) pendingUploads.current.set(tempId, blobUrl);
      return {
        file_id: tempId, name: f.name, mime: f.type || 'application/octet-stream',
        url: blobUrl, uploading: true,
      };
    });
    setAttachments(a => [...a, ...optimistic]);
    setPlusOpen(false);

    // Fire the actual upload in the background.
    const fd = new FormData();
    for (const f of fileArr) fd.append('files', f);
    if (convForUpload) fd.append('conversation_id', convForUpload);
    try {
      const r = await fetch(`${API_BASE}/api/llm/uploads`, { method: 'POST', headers: authHeaders(), body: fd });
      if (r.ok) {
        const d = await r.json();
        const serverFiles = d.files || [];
        // Swap the temp chips with server rows (positional match by order).
        setAttachments(a => {
          const tempIds = new Set(optimistic.map(o => o.file_id));
          const next = a.filter(x => !tempIds.has(x.file_id));
          return [...next, ...serverFiles];
        });
        // Revoke blob URLs to free memory.
        optimistic.forEach(o => {
          const b = pendingUploads.current.get(o.file_id);
          if (b) { URL.revokeObjectURL(b); pendingUploads.current.delete(o.file_id); }
        });
      } else {
        // Mark temp chips as failed so the user sees what happened.
        setAttachments(a => a.map(x =>
          optimistic.some(o => o.file_id === x.file_id)
            ? { ...x, uploading: false, failed: true }
            : x
        ));
      }
    } catch (e) {
      setAttachments(a => a.map(x =>
        optimistic.some(o => o.file_id === x.file_id)
          ? { ...x, uploading: false, failed: true }
          : x
      ));
    }
  };

  const removeAttachment = useCallback((fid) => {
    // Revoke the blob URL (if we made one) to avoid a memory leak.
    const blobUrl = pendingUploads.current.get(fid);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      pendingUploads.current.delete(fid);
    }
    setAttachments(a => a.filter(x => x.file_id !== fid));
  }, []);

  const deleteConv = async (cid) => {
    if (!window.confirm('Delete this conversation?')) return;
    await fetch(`${API_BASE}/api/llm/conversations/${cid}`, { method: 'DELETE', headers: authHeaders() });
    if (cid === activeId) startNewChat();
    refreshConvs(convFilter);
  };

  // ── Image generation via modal (kept for explicit count/aspect picking) ──
  const generateImages = async () => {
    const prompt = imgPrompt.trim();
    if (!prompt) return;
    setImagePromptOpen(false);
    const tmpUserId = `u-${Date.now()}`;
    const tmpCardId = `img-${Date.now()}`;
    setMessages(m => [
      ...m,
      { id: tmpUserId, role: 'user', content: `Generate images: ${prompt}`, ts: new Date().toISOString() },
      { id: tmpCardId, role: 'assistant', type: 'image_grid', prompt, count: imgCount, size: imgSize, status: 'running', slides: [], ts: new Date().toISOString() },
    ]);
    setImgPrompt('');
    const imgRefs = attachments.filter(a => (a.mime || '').startsWith('image/')).map(a => ({ url: a.url, mime: a.mime }));
    setAttachments([]);
    try {
      const r = await fetch(`${API_BASE}/api/llm/images`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt, count: imgCount, size: imgSize, quality: 'medium',
          conversation_id: activeId || undefined,
          reference_files: imgRefs.length ? imgRefs : undefined,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const respJson = await r.json();
      const { job_id, user_message_id, assistant_message_id, conversation_id: newConvId, conversation_created } = respJson;
      const cardId = assistant_message_id || tmpCardId;
      if (assistant_message_id || user_message_id) {
        setMessages(m => m.map(x => {
          if (x.id === tmpUserId && user_message_id) return { ...x, id: user_message_id };
          if (x.id === tmpCardId && assistant_message_id) return { ...x, id: assistant_message_id };
          return x;
        }));
      }
      if (conversation_created && newConvId && !activeId) {
        setActiveConv(c => c || { id: newConvId, provider, model, title: '' });
        window.history.replaceState({}, '', `/llm-studio/${newConvId}`);
        refreshConvs(convFilter);
      }
      for (let i = 0; i < 100; i++) {
        await new Promise(res => setTimeout(res, 3000));
        const pr = await fetch(`${API_BASE}/api/llm/images/${job_id}`, { headers: authHeaders() });
        if (!pr.ok) continue;
        const doc = await pr.json();
        setMessages(m => m.map(x => x.id === cardId ? { ...x, status: doc.status, slides: doc.slides || [], errors: doc.errors || [] } : x));
        if (doc.status === 'done' || doc.status === 'error') break;
      }
    } catch (e) {
      setMessages(m => m.map(x => x.id === tmpCardId ? { ...x, status: 'error', error: e.message || 'Request failed' } : x));
    }
  };

  const providers = useMemo(() => {
    const rows = [];
    for (const m of catalog.anthropic || []) rows.push({ provider: 'anthropic', ...m });
    for (const m of catalog.openai || []) rows.push({ provider: 'openai', ...m });
    return rows;
  }, [catalog]);
  const activeModelLabel = providers.find(p => p.id === model)?.label || model;

  const filteredConvs = conversations.filter(c => (c.title || '').toLowerCase().includes(convFilter.toLowerCase()));
  const buckets = bucketize(filteredConvs);
  const isEmpty = messages.length === 0 && !streaming;

  // ═══════════════════ RENDER ═══════════════════
  // Compute drawer transform + backdrop opacity for the mobile
  // gesture layer. On desktop these are inert (drawer flows in the
  // grid; no backdrop).
  const currentDrawerX = dragX != null
    ? dragX
    : (sidebarOpen ? SIDEBAR_W : 0);
  const progress = currentDrawerX / SIDEBAR_W; // 0…1
  const backdropOpacity = isMobile ? (progress * 0.4) : 0;
  const dragging = dragX != null;

  return (
    <div
      className="h-screen w-screen flex overflow-hidden relative"
      style={{ background: T.bg, color: T.primary, fontFamily: '-apple-system, "Segoe UI", Inter, sans-serif', touchAction: isMobile ? 'pan-y' : 'auto' }}
      data-testid="llm-studio-page"
      onClick={() => plusOpen && setPlusOpen(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUpOrCancel}
      onPointerCancel={onPointerUpOrCancel}
    >
      {/* ────────── SIDEBAR ────────── */}
      <aside
        className="flex-shrink-0 overflow-hidden flex flex-col"
        style={
          isMobile
            ? {
                position: 'absolute', top: 0, bottom: 0, left: 0,
                width: SIDEBAR_W, zIndex: 40,
                transform: `translateX(${currentDrawerX - SIDEBAR_W}px)`,
                transition: dragging ? 'none' : 'transform 220ms ease-out',
                background: T.sidebar,
              }
            : {
                width: sidebarOpen ? SIDEBAR_W : 0,
                transition: 'width 0.25s ease',
                background: T.sidebar,
              }
        }
      >
        <div className="flex flex-col" style={{ width: 260, height: '100%' }}>
          {/* Sidebar top bar: collapse + new chat (spec §3.1) */}
          <div className="flex items-center justify-between px-3 pt-3 pb-1">
            <IconButton onClick={() => setSidebarOpen(false)} title="Collapse sidebar" testId="llm-studio-collapse">
              <PanelLeft className="w-[18px] h-[18px]" />
            </IconButton>
            <IconButton onClick={startNewChat} title="New chat" testId="llm-studio-new-chat">
              <PenSquare className="w-[18px] h-[18px]" />
            </IconButton>
          </div>

          {/* Search — inline, ghost style */}
          <div className="px-3 py-2">
            <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg" style={{ color: T.secondary }}>
              <Search className="w-4 h-4 flex-shrink-0" />
              <input
                value={convFilter}
                onChange={(e) => { setConvFilter(e.target.value); refreshConvs(e.target.value); }}
                placeholder="Search chats"
                className="flex-1 bg-transparent border-0 outline-none text-sm"
                style={{ color: T.primary }}
                data-testid="llm-studio-search"
              />
            </div>
          </div>

          {/* Grouped conversation list (Today / Yesterday / Previous 7 days / etc.) */}
          <div className="flex-1 overflow-y-auto px-2 pb-2" data-testid="llm-studio-past-chats">
            {Object.entries(buckets).map(([label, items]) => items.length > 0 && (
              <div key={label} className="mb-1">
                <div
                  className="px-3 pt-3 pb-1 text-[12px] font-semibold uppercase tracking-wide"
                  style={{ color: T.tertiary }}
                >{label}</div>
                {items.map(c => (
                  <ConvRow
                    key={c.id}
                    c={c}
                    active={c.id === activeId}
                    onOpen={() => navigate(`/llm-studio/${c.id}`)}
                    onDelete={() => deleteConv(c.id)}
                  />
                ))}
              </div>
            ))}
            {filteredConvs.length === 0 && (
              <div className="p-6 text-xs text-center" style={{ color: T.tertiary }}>No conversations yet.</div>
            )}
          </div>

          {/* Profile footer */}
          <div className="flex items-center gap-3 px-3 py-3" style={{ borderTop: `1px solid ${T.border}` }}>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold"
              style={{ background: T.userBubble, color: T.primary }}
            >R</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">Studio</div>
              <div className="text-[12px]" style={{ color: T.tertiary }}>LLM Studio</div>
            </div>
            <Link
              to="/"
              className="p-1.5 rounded-md hover:opacity-100"
              style={{ color: T.secondary }}
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile backdrop — opacity tracks the drag progress 1:1
          (spec §4). Tap closes the drawer. Sits between the drawer
          (z=40) and the main content so pointer events on the drawer
          still work. Rendered on desktop with opacity 0 +
          pointer-events none so it's a cheap no-op there. */}
      <div
        onClick={() => { if (isMobile && sidebarOpen) setSidebarOpen(false); }}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          opacity: backdropOpacity,
          pointerEvents: (isMobile && (sidebarOpen || dragging)) ? 'auto' : 'none',
          transition: dragging ? 'none' : 'opacity 220ms ease-out',
          zIndex: 30,
        }}
        data-testid="llm-studio-mobile-backdrop"
        aria-hidden={!(sidebarOpen && isMobile)}
      />


      {/* ────────── MAIN ────────── */}
      <main className="flex-1 flex flex-col min-w-0 relative">
        {/* Header (spec §2 — fixed top; model picker + avatar) */}
        <header className="flex items-center justify-between px-4 h-[52px] flex-shrink-0">
          <div className="flex items-center gap-1.5">
            {!sidebarOpen && (
              <IconButton onClick={() => setSidebarOpen(true)} title="Open sidebar" testId="llm-studio-open-sidebar">
                <PanelLeft className="w-[18px] h-[18px]" />
              </IconButton>
            )}
            {/* Brand wordmark — sits between the sidebar toggle and the model
                picker. Non-interactive; identifies this workspace as LLM Studio
                so operators arriving from the main app know where they are. */}
            <div
              className="flex items-center gap-1.5 pl-1 pr-2.5 select-none"
              data-testid="llm-studio-brandmark"
            >
              <Sparkles className="w-[15px] h-[15px]" style={{ color: T.accentFg }} />
              <span
                className="text-[14px] font-semibold tracking-tight"
                style={{ color: T.primary }}
              >LLM Studio</span>
            </div>
            <div className="w-px h-5 mx-1" style={{ background: T.border }} />
            <div className="relative">
              <button
                onClick={() => setModelOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-colors text-[17px] font-semibold"
                style={{ color: T.secondary, background: modelOpen ? T.hover : 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
                onMouseLeave={e => { e.currentTarget.style.background = modelOpen ? T.hover : 'transparent'; }}
                data-testid="llm-studio-model-dropdown"
              >
                <span>{activeModelLabel}</span>
                <ChevronDown className="w-[14px] h-[14px]" />
              </button>
              {modelOpen && (
                <div
                  className="absolute top-full left-0 mt-1 w-[300px] rounded-2xl z-30 overflow-hidden py-1"
                  style={{ background: T.bg, border: `1px solid ${T.border}`, boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}
                >
                  <SectionLabel>Anthropic</SectionLabel>
                  {(catalog.anthropic || []).map(m => (
                    <ModelRow key={m.id} m={m} active={model === m.id} onClick={() => { if (!m.restricted) { setProvider('anthropic'); setModel(m.id); setModelOpen(false); } }} />
                  ))}
                  <div className="my-1" style={{ borderTop: `1px solid ${T.border}` }} />
                  <SectionLabel>OpenAI</SectionLabel>
                  {(catalog.openai || []).map(m => (
                    <ModelRow key={m.id} m={m} active={model === m.id} onClick={() => { setProvider('openai'); setModel(m.id); setModelOpen(false); }} />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[13px] font-bold"
              style={{ background: T.userBubble, color: T.primary }}
              data-testid="llm-studio-avatar"
            >R</div>
          </div>
        </header>

        {/* Body: either empty greeting or the message stream */}
        {isEmpty ? (
          <EmptyState
            onSuggest={(prompt) => send(prompt)}
            composer={
              <Composer
                mode="center"
                input={input} setInput={setInput} taRef={taRef} autoGrow={autoGrow}
                onSend={() => send()} onStop={stopStream} streaming={streaming}
                plusOpen={plusOpen} setPlusOpen={setPlusOpen}
                onUploadPick={() => fileInputRef.current?.click()}
                onCreateImage={() => setImagePromptOpen(true)}
                onWebSearchPin={() => setUseWebSearch(v => !v)}
                webPinned={useWebSearch}
                imgPinned={imgMode}
                onImgPinToggle={() => setImgMode(v => !v)}
                attachments={attachments} onAttachmentRemove={removeAttachment}
                imgCount={imgCount} setImgCount={setImgCount}
                imgSize={imgSize} setImgSize={setImgSize}
              />
            }
          />
        ) : (
          <>
            <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="llm-studio-message-list">
              <div className="mx-auto w-full" style={{ maxWidth: 768, padding: '20px 16px 24px' }}>
                {messages.map(m => <MessageBubble key={m.id} m={m} />)}
                {streaming && messages[messages.length - 1]?.role === 'user' && (
                  <div className="flex gap-1.5 my-3 ml-1" data-testid="llm-studio-thinking-dots">
                    {[0,1,2].map(i => (
                      <span
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{ background: T.secondary, animation: `llmStudioPulse 1.2s ${i * 0.18}s infinite ease-in-out` }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-shrink-0" style={{ padding: '8px 0 12px' }}>
              <Composer
                mode="bottom"
                input={input} setInput={setInput} taRef={taRef} autoGrow={autoGrow}
                onSend={() => send()} onStop={stopStream} streaming={streaming}
                plusOpen={plusOpen} setPlusOpen={setPlusOpen}
                onUploadPick={() => fileInputRef.current?.click()}
                onCreateImage={() => setImagePromptOpen(true)}
                onWebSearchPin={() => setUseWebSearch(v => !v)}
                webPinned={useWebSearch}
                imgPinned={imgMode}
                onImgPinToggle={() => setImgMode(v => !v)}
                attachments={attachments} onAttachmentRemove={removeAttachment}
                imgCount={imgCount} setImgCount={setImgCount}
                imgSize={imgSize} setImgSize={setImgSize}
              />
              <div
                className="text-center text-[12px] px-4 pt-2"
                style={{ color: T.tertiary }}
                data-testid="llm-studio-disclaimer"
              >AI can make mistakes. Check important info.</div>
            </div>
          </>
        )}

        {/* Shared hidden file input (uploads via + menu) */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,application/pdf,.txt,.md,.csv,.json"
          className="hidden"
          onChange={(e) => { onUploadFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
          data-testid="llm-studio-file-input"
        />
      </main>

      {/* Image-gen full modal */}
      {imagePromptOpen && (
        <ImageGenModal
          prompt={imgPrompt} setPrompt={setImgPrompt}
          count={imgCount} setCount={setImgCount}
          size={imgSize} setSize={setImgSize}
          onCancel={() => setImagePromptOpen(false)}
          onSubmit={generateImages}
        />
      )}

      {/* Keyframes / scrollbar */}
      <style>{`
        @keyframes llmStudioPulse { 0%,80%,100% { opacity:0.3; transform: scale(0.85); } 40% { opacity: 1; transform: scale(1); } }
        @keyframes llmStudioShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .llmstudio-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .llmstudio-scroll::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 4px; }
        textarea::placeholder, input::placeholder { color: ${T.tertiary}; opacity: 1; }
      `}</style>
    </div>
  );
}

// ═══════════════════ SUBCOMPONENTS ═══════════════════

// Ghost icon button used in the sidebar top bar & main header.
function IconButton({ children, onClick, title, testId, active }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="p-2 rounded-md transition-colors flex items-center justify-center"
      style={{ color: T.secondary, background: active ? T.hover : 'transparent' }}
      onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? T.hover : 'transparent'; }}
    >{children}</button>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: T.tertiary }}>
      {children}
    </div>
  );
}

function ModelRow({ m, active, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={m.restricted}
      className="w-full flex items-center justify-between px-3 py-2 text-left transition-colors"
      style={{
        background: active ? T.hover : 'transparent',
        color: m.restricted ? T.tertiary : T.primary,
        opacity: m.restricted ? 0.5 : 1,
        cursor: m.restricted ? 'not-allowed' : 'pointer',
      }}
      onMouseEnter={e => { if (!m.restricted) e.currentTarget.style.background = T.hover; }}
      onMouseLeave={e => { if (!m.restricted && !active) e.currentTarget.style.background = 'transparent'; }}
      data-testid={`llm-studio-model-${m.id}`}
    >
      <div className="flex flex-col min-w-0">
        <div className="text-sm font-medium truncate">{m.label}</div>
        <div className="text-[11px] mt-0.5 truncate" style={{ color: T.tertiary }}>
          {Math.floor((m.context || 0) / 1000)}k ctx{m.hint ? ` · ${m.hint}` : ''}
        </div>
      </div>
      {active && <Check className="w-4 h-4 flex-shrink-0 ml-2" style={{ color: T.accentFg }} />}
    </button>
  );
}

function ConvRow({ c, active, onOpen, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group flex items-center gap-2 mx-1 px-2.5 py-2 rounded-lg cursor-pointer transition-colors"
      style={{ background: active ? T.hover : (hover ? T.hover : 'transparent') }}
      data-testid={`llm-studio-conv-${c.id}`}
    >
      <div
        className="flex-1 min-w-0 text-sm truncate"
        style={{ color: active ? T.primary : T.primary }}
      >
        {c.title || 'Untitled'}
      </div>
      {hover && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 rounded"
          style={{ color: T.tertiary }}
          title="Delete conversation"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ═══════════════════ COMPOSER ═══════════════════
function Composer({
  mode, input, setInput, taRef, autoGrow,
  onSend, onStop, streaming,
  plusOpen, setPlusOpen,
  onUploadPick, onCreateImage, onWebSearchPin, webPinned,
  imgPinned, onImgPinToggle,
  attachments, onAttachmentRemove,
  imgCount, setImgCount, imgSize, setImgSize,
}) {
  const hasContent = (input || '').trim().length > 0 || (attachments || []).length > 0;
  const anyUploading = (attachments || []).some(a => a.uploading);
  const canSend = hasContent && !anyUploading;
  return (
    <div className="mx-auto w-full relative" style={{ maxWidth: 768, padding: '0 16px' }}>
      {plusOpen && (
        <div
          className="absolute left-4 z-20 w-[260px] rounded-2xl overflow-hidden py-1"
          style={{
            background: T.bg,
            border: `1px solid ${T.border}`,
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
            bottom: '100%', marginBottom: 8,
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="llm-studio-plus-menu"
        >
          <MenuItem icon={<Paperclip className="w-[17px] h-[17px]" />} label="Upload photos & files" onClick={() => { onUploadPick(); setPlusOpen(false); }} testId="llm-studio-menu-upload-files" />
          <MenuItem icon={<Sparkles className="w-[17px] h-[17px]" />} label="Create image" sub={`gpt-image-2 · ${imgCount}·${imgSize.replace('1024x1024','1:1').replace('1536x1024','16:9').replace('1024x1536','9:16')}`} onClick={() => { onCreateImage(); setPlusOpen(false); }} testId="llm-studio-menu-generate-images" />
          <MenuItem icon={<Globe className="w-[17px] h-[17px]" />} label={webPinned ? 'Web search (pinned)' : 'Pin web search'} sub={webPinned ? 'Next reply will consult live web sources' : 'Next reply will consult live web sources'} active={webPinned} onClick={() => { onWebSearchPin(); setPlusOpen(false); }} testId="llm-studio-menu-web" />
          <MenuItem icon={<ImageIcon className="w-[17px] h-[17px]" />} label={imgPinned ? 'Image mode (pinned)' : 'Pin image mode'} sub="Send fires gpt-image-2 with current settings" active={imgPinned} onClick={() => { onImgPinToggle(); setPlusOpen(false); }} testId="llm-studio-menu-img" />
        </div>
      )}

      {/* Composer card */}
      <div
        className="relative"
        style={{
          background: T.userBubble,
          border: `1px solid ${T.border}`,
          borderRadius: 28,
          padding: '10px 12px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
        }}
      >
        {/* Attachment chips + pinned-tool chips (spec §3.2 — chips above textarea) */}
        {(attachments.length > 0 || webPinned || imgPinned) && (
          <div className="flex flex-wrap gap-2 pb-2" data-testid="llm-studio-attachments">
            {webPinned && (
              <PinChip icon={<Globe className="w-3.5 h-3.5" />} label="Web search" onRemove={onWebSearchPin} testId="llm-studio-chip-web" />
            )}
            {imgPinned && (
              <PinChip icon={<Sparkles className="w-3.5 h-3.5" />} label={`Image · ${imgCount}·${imgSize.replace('1024x1024','1:1').replace('1536x1024','16:9').replace('1024x1536','9:16')}`} onRemove={onImgPinToggle} testId="llm-studio-chip-img"
                extra={
                  <div className="flex items-center gap-1 pl-1" onClick={(e) => e.stopPropagation()}>
                    <select value={imgCount} onChange={(e) => setImgCount(Number(e.target.value))} className="bg-transparent border-0 outline-none text-[11px]" style={{ color: T.accentFg }} data-testid="llm-studio-img-count-chip">
                      {[1,2,3,4,6,9].map(n => <option key={n} value={n} style={{ background: T.bg, color: T.primary }}>{n}</option>)}
                    </select>
                    <select value={imgSize} onChange={(e) => setImgSize(e.target.value)} className="bg-transparent border-0 outline-none text-[11px]" style={{ color: T.accentFg }} data-testid="llm-studio-img-size-chip">
                      <option value="1024x1024" style={{ background: T.bg }}>1:1</option>
                      <option value="1536x1024" style={{ background: T.bg }}>16:9</option>
                      <option value="1024x1536" style={{ background: T.bg }}>9:16</option>
                    </select>
                  </div>
                }
              />
            )}
            {attachments.map(a => {
              const isImg = (a.mime || '').startsWith('image/');
              return (
                <div
                  key={a.file_id}
                  className="relative flex items-center gap-2 rounded-xl overflow-hidden"
                  style={{
                    background: T.hover,
                    border: `1px solid ${a.failed ? T.errBd : T.border}`,
                    padding: isImg ? 0 : '8px 10px',
                  }}
                  data-testid="llm-studio-attachment-chip"
                >
                  {isImg ? (
                    <img
                      src={a.url}
                      alt={a.name}
                      className="w-14 h-14 object-cover"
                      data-testid="llm-studio-attachment-thumb"
                    />
                  ) : (
                    <>
                      <Paperclip className="w-3.5 h-3.5" style={{ color: T.secondary }} />
                      <span className="text-[13px] max-w-[160px] truncate" style={{ color: T.primary }}>{a.name}</span>
                    </>
                  )}
                  {a.uploading && (
                    <div
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{ background: 'rgba(255,255,255,0.35)' }}
                    >
                      <div
                        className="w-4 h-4 rounded-full animate-spin"
                        style={{ border: '2px solid rgba(0,0,0,0.15)', borderTopColor: T.primary }}
                      />
                    </div>
                  )}
                  {a.failed && (
                    <div
                      className="absolute bottom-0 left-0 right-0 text-[9px] text-center py-0.5 pointer-events-none"
                      style={{ background: T.errBg, color: T.errFg }}
                    >Upload failed</div>
                  )}
                  <button
                    onClick={() => onAttachmentRemove(a.file_id)}
                    className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(0,0,0,0.65)', color: '#fff' }}
                    data-testid="llm-studio-attachment-remove"
                  ><X className="w-2.5 h-2.5" /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Row: + / textarea / mic / send */}
        <div className="flex items-end gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setPlusOpen(v => !v); }}
            className="p-2 rounded-full transition-colors flex-shrink-0"
            style={{ color: T.secondary, background: plusOpen ? T.hover : 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
            onMouseLeave={e => { e.currentTarget.style.background = plusOpen ? T.hover : 'transparent'; }}
            title="Add"
            data-testid="llm-studio-plus-button"
          ><Plus className="w-5 h-5" /></button>

          <textarea
            ref={taRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); autoGrow(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            placeholder={imgPinned ? 'Describe the image…' : (webPinned ? 'Ask something to research…' : 'Ask anything')}
            rows={1}
            className="flex-1 bg-transparent border-0 outline-none resize-none py-1.5 px-1"
            style={{ color: T.primary, fontSize: 16, lineHeight: '24px', maxHeight: 200, fontFamily: 'inherit' }}
            data-testid="llm-studio-input"
          />

          <button
            className="p-2 rounded-full flex-shrink-0"
            style={{ color: T.secondary }}
            title="Voice (coming soon)"
            data-testid="llm-studio-mic"
            onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          ><Mic className="w-[18px] h-[18px]" /></button>

          {streaming ? (
            <button
              onClick={onStop}
              className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ background: T.sendActive, color: '#FFFFFF' }}
              title="Stop"
              data-testid="llm-studio-stop"
            ><Square className="w-3 h-3" fill="currentColor" /></button>
          ) : (
            <button
              onClick={onSend}
              disabled={!canSend}
              className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              style={{
                background: canSend ? T.sendActive : T.sendIdle,
                color: canSend ? '#FFFFFF' : T.tertiary,
                cursor: canSend ? 'pointer' : 'default',
              }}
              title={anyUploading ? 'Waiting for upload…' : 'Send'}
              data-testid="llm-studio-send"
            ><ArrowUp className="w-[18px] h-[18px]" strokeWidth={2.5} /></button>
          )}
        </div>
      </div>

      {mode === 'center' && (
        <div className="text-center text-[12px] mt-2" style={{ color: T.tertiary }}>
          AI can make mistakes. Check important info.
        </div>
      )}
    </div>
  );
}

// Pinned-tool chip (blue accent — the ONLY colour deviation per spec).
function PinChip({ icon, label, onRemove, testId, extra }) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full text-[12px] font-medium"
      style={{
        background: T.accentBg,
        border: `1px solid ${T.accentBd}`,
        color: T.accentFg,
        padding: '5px 8px 5px 10px',
      }}
      data-testid={testId}
    >
      {icon}
      <span>{label}</span>
      {extra}
      <button
        onClick={onRemove}
        className="p-0.5 rounded-full ml-0.5"
        style={{ color: T.accentFg }}
        title="Remove"
      ><X className="w-3 h-3" /></button>
    </div>
  );
}

function MenuItem({ icon, label, sub, active, onClick, testId }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
      style={{ background: active ? T.accentBg : (hover ? T.hover : 'transparent') }}
      data-testid={testId}
    >
      <span style={{ color: active ? T.accentFg : T.secondary, display: 'flex' }}>{icon}</span>
      <span className="flex-1 min-w-0">
        <div className="text-[14px] font-medium truncate" style={{ color: active ? T.accentFg : T.primary }}>{label}</div>
        {sub && <div className="text-[12px] truncate" style={{ color: T.tertiary }}>{sub}</div>}
      </span>
    </button>
  );
}

// ═══════════════════ SHARED IMAGE LIGHTBOX ═══════════════════
// Full-screen image viewer. Closes on:
//   - Click on the dark backdrop (outside the image itself)
//   - Pressing Escape
// Clicks INSIDE the image bubble bubble up harmlessly (they'd also close,
// which matches ChatGPT's UX — the image itself has cursor-zoom-out).
function Lightbox({ url, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-8 cursor-zoom-out"
      style={{ background: 'rgba(0,0,0,0.9)' }}
      onClick={onClose}
      data-testid="llm-studio-lightbox"
    >
      <img
        src={url}
        alt={alt || 'image'}
        className="max-w-full max-h-full object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
        style={{ background: 'rgba(255,255,255,0.15)', color: '#FFF' }}
        title="Close (Esc)"
        data-testid="llm-studio-lightbox-close"
      ><X className="w-5 h-5" /></button>
    </div>
  );
}

// ═══════════════════ EMPTY STATE ═══════════════════
const SUGGESTIONS = [
  { icon: <Sparkles className="w-[15px] h-[15px]" />, label: 'Create an image',    prompt: 'Generate an image of a cinematic Mumbai monsoon street at dusk' },
  { icon: <PenSquare className="w-[15px] h-[15px]" />, label: 'Help me write',     prompt: 'Help me write a short professional bio' },
  { icon: <Globe className="w-[15px] h-[15px]" />,     label: 'Research a topic',  prompt: 'Give me a research briefing on synthetic biology in 2026' },
  { icon: <MessageSquare className="w-[15px] h-[15px]" />, label: 'Brainstorm',    prompt: 'Brainstorm 5 marketing campaign ideas for a sneaker brand' },
];

function EmptyState({ composer, onSuggest }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-6 px-4" data-testid="llm-studio-empty">
      <div className="text-[28px] font-semibold" style={{ color: T.primary }}>What can I help with?</div>
      <div className="w-full">{composer}</div>
      <div className="flex flex-wrap justify-center gap-2 max-w-2xl">
        {SUGGESTIONS.map(s => (
          <button
            key={s.label}
            onClick={() => onSuggest(s.prompt)}
            className="flex items-center gap-2 px-4 py-2 rounded-full transition-colors text-[14px]"
            style={{ background: 'transparent', border: `1px solid ${T.border}`, color: T.secondary }}
            onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            data-testid={`llm-studio-suggest-${s.label.toLowerCase().replace(/\s+/g,'-')}`}
          >
            {s.icon}
            <span>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════ MESSAGE BUBBLES ═══════════════════
function MessageBubble({ m }) {
  const [copied, setCopied] = useState(false);
  const [hover, setHover] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  if (m.type === 'image_grid') return <ImageGridCard m={m} />;

  const copy = () => {
    try { navigator.clipboard.writeText(m.content || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { /* no-op */ }
  };

  if (m.role === 'user') {
    return (
      <div
        className="flex justify-end my-4"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid="llm-studio-message-user"
      >
        <div className="flex flex-col items-end gap-1.5" style={{ maxWidth: '75%' }}>
          {(m.attachments || []).filter(a => (a.mime || '').startsWith('image/')).map((a, i) => (
            <img
              key={i}
              src={a.url}
              alt={a.name}
              className="rounded-2xl cursor-zoom-in transition-opacity hover:opacity-90"
              style={{ maxWidth: 220, border: `1px solid ${T.border}` }}
              onClick={() => setLightboxUrl(a.url)}
              data-testid="llm-studio-user-image"
            />
          ))}
          {(m.attachments || []).filter(a => !(a.mime || '').startsWith('image/')).map((a, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl text-[13px]" style={{ background: T.userBubble, padding: '8px 12px' }}>
              <Paperclip className="w-3.5 h-3.5" style={{ color: T.secondary }} />
              <span>{a.name}</span>
            </div>
          ))}
          {m.content && m.content !== '(attachments)' && (
            <div
              className="whitespace-pre-wrap text-[16px] leading-[1.7]"
              style={{ background: T.userBubble, color: T.primary, padding: '10px 18px', borderRadius: 20 }}
            >{m.content}</div>
          )}
        </div>
        {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      </div>
    );
  }

  // Assistant — full-width, no bubble, left-aligned (spec §3.2)
  return (
    <div
      className="my-4"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="llm-studio-message-assistant"
    >
      <div className="text-[16px] leading-[1.7]" style={{ color: T.primary }}>
        {renderMarkdown(m.content || '')}
        {m.streaming && !m.content && !(m.pending_images && m.pending_images.length) && !(m.images && m.images.length) && (
          <span className="inline-block w-[3px] h-[16px] ml-0.5 align-middle" style={{ background: T.primary, animation: 'llmStudioPulse 1s infinite' }} />
        )}
        {m.streaming && m.content && (
          <span className="inline-block w-[3px] h-[16px] ml-0.5 align-middle" style={{ background: T.primary, animation: 'llmStudioPulse 1s infinite' }} />
        )}
      </div>

      {/* 2026-05-23 (Rohit E1) — image tool cards. Shimmer skeleton while
          `pending_images` are running; rounded image card once
          `tool_result` lands. Stacks vertically with 12px gap. */}
      {(m.pending_images || []).map(p => (
        <div
          key={`pending-${p.tool_call_id}`}
          className="mt-3 relative overflow-hidden rounded-2xl"
          style={{ maxWidth: 440, background: T.userBubble, border: `1px solid ${T.border}`, aspectRatio: '16/10' }}
          data-testid={`llm-studio-image-pending-${p.tool_call_id}`}
        >
          <div className="absolute inset-0" style={{ animation: 'llmStudioShimmer 1.6s infinite linear', background: `linear-gradient(90deg, ${T.userBubble} 0%, ${T.hover} 50%, ${T.userBubble} 100%)`, backgroundSize: '200% 100%' }} />
          <div className="absolute inset-0 flex items-center justify-center gap-2 z-10" style={{ color: T.secondary }}>
            <Sparkles className="w-4 h-4" />
            <span className="text-[13px]">Creating image…</span>
          </div>
        </div>
      ))}

      {(m.images || m.attachments || []).filter(a => (a.mime || 'image/png').startsWith('image/')).map((img, i) => (
        <ImageCard key={img.image_id || img.url || i} url={img.url} />
      ))}

      {(m.image_errors || []).map((err, i) => (
        <div
          key={`img-err-${i}`}
          className="mt-3 text-[13px] rounded-lg px-3 py-2"
          style={{ background: T.errBg, border: `1px solid ${T.errBd}`, color: T.errFg, maxWidth: 440 }}
        >{err.message}</div>
      ))}

      {m.error && (
        <div
          className="mt-2 text-[13px] rounded-lg px-3 py-2"
          style={{ background: T.errBg, border: `1px solid ${T.errBd}`, color: T.errFg }}
        >{m.error}</div>
      )}
      {!m.streaming && m.content && (
        <div className="mt-1.5" style={{ opacity: hover ? 1 : 0, transition: 'opacity 0.15s' }}>
          <button
            onClick={copy}
            className="flex items-center gap-1 text-[12px] px-1.5 py-1 rounded-md"
            style={{ color: T.tertiary }}
            onMouseEnter={e => { e.currentTarget.style.background = T.hover; e.currentTarget.style.color = T.primary; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.tertiary; }}
            title="Copy"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
    </div>
  );
}

// Chat-inline image card — spec §4.2 (max-width 440, 16px radius,
// hover download button top-right, one-line caption below).
function ImageCard({ url }) {
  const [hover, setHover] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  return (
    <>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="mt-3 relative overflow-hidden rounded-2xl cursor-zoom-in"
        style={{ maxWidth: 440, background: T.userBubble, border: `1px solid ${T.border}` }}
        data-testid="llm-studio-image-card"
      >
        <img
          src={url}
          alt="Generated by generate_image"
          className="w-full h-auto block"
          onClick={() => setLightbox(true)}
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg transition-opacity text-[11px] font-semibold"
          style={{
            background: 'rgba(0,0,0,0.65)', color: '#FFF',
            opacity: hover ? 1 : 0, backdropFilter: 'blur(6px)',
          }}
          title="Download"
          data-testid="llm-studio-image-download"
        >
          <Upload className="w-3 h-3 rotate-180" /> Download
        </a>
      </div>
      {lightbox && (
        <Lightbox url={url} alt="Generated by generate_image" onClose={() => setLightbox(false)} />
      )}
    </>
  );
}

// Tiny markdown renderer — code fences, headings, lists, inline bold/code.
function renderMarkdown(text) {
  const parts = text.split(/(```[\s\S]*?```)/g);
  return parts.map((part, i) => {
    if (part.startsWith('```')) {
      const code = part.replace(/```[a-z]*\n?/i, '').replace(/```$/, '');
      return (
        <pre
          key={i}
          className="my-3 p-4 rounded-lg overflow-x-auto font-mono text-[13px] leading-[1.6]"
          style={{ background: '#0D0D0D', color: '#ECECEC' }}
        >{code}</pre>
      );
    }
    return part.split('\n').map((line, j) => {
      if (!line.trim()) return <div key={`${i}-${j}`} className="h-2" />;
      if (/^#{1,3} /.test(line)) {
        const level = line.match(/^#+/)[0].length;
        return <div key={`${i}-${j}`} className="font-semibold" style={{ fontSize: level === 1 ? 20 : level === 2 ? 17 : 15, margin: '14px 0 6px' }}>{inlineMd(line.replace(/^#+ /, ''))}</div>;
      }
      const bullet = /^[-*] /.test(line.trim());
      const num = /^\d+\. /.test(line.trim());
      if (bullet || num) {
        return (
          <div key={`${i}-${j}`} className="flex gap-2 my-1 pl-2">
            <span className="flex-shrink-0" style={{ color: T.secondary }}>{bullet ? '•' : line.trim().match(/^\d+\./)[0]}</span>
            <span>{inlineMd(line.replace(/^[-*] /, '').replace(/^\d+\. /, ''))}</span>
          </div>
        );
      }
      return <div key={`${i}-${j}`} className="my-0.5">{inlineMd(line)}</div>;
    });
  });
}
function inlineMd(line) {
  const tokens = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return tokens.map((t, k) => {
    if (t.startsWith('**')) return <strong key={k}>{t.slice(2, -2)}</strong>;
    if (t.startsWith('`')) return <code key={k} className="font-mono text-[0.9em] px-1.5 py-0.5 rounded" style={{ background: T.hover }}>{t.slice(1, -1)}</code>;
    return t;
  });
}

// ═══════════════════ IMAGE GRID CARD ═══════════════════
function ImageGridCard({ m }) {
  const [lightbox, setLightbox] = useState(null);
  const slides = m.slides || [];
  const total = m.count || slides.length || 0;
  const done = m.status === 'done';
  const err = m.status === 'error';

  return (
    <div className="my-4" data-testid={`llm-studio-image-card-${m.id}`}>
      <div className="rounded-2xl overflow-hidden" style={{ background: T.userBubble, border: `1px solid ${T.border}` }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: `1px solid ${T.border}` }}>
          <div className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: T.accentFg }}>
            {done ? `Slides 1–${slides.length} of ${total}` : err ? 'Failed' : `Generating ${slides.length}/${total}`}
          </div>
          {done && slides.length > 0 && (
            <button
              className="text-[12px] font-semibold px-2.5 py-1 rounded-lg"
              style={{ background: T.accentBg, color: T.accentFg, border: `1px solid ${T.accentBd}` }}
              onClick={() => setLightbox(slides[0])}
            >Open full gallery</button>
          )}
        </div>
        <div className="px-4 py-2 text-[13px] italic" style={{ color: T.tertiary, borderBottom: `1px solid ${T.border}` }}>
          &ldquo;{m.prompt}&rdquo;
        </div>
        <div className="p-3">
          {err ? (
            <div className="p-3 text-[13px] rounded-lg" style={{ background: T.errBg, border: `1px solid ${T.errBd}`, color: T.errFg }}>
              {m.error || 'Generation failed'}
              {m.errors?.length > 0 && (
                <ul className="mt-1 text-[11px] list-disc pl-4">
                  {m.errors.map((e, i) => <li key={i}>Slot {e.index}: {e.error}</li>)}
                </ul>
              )}
            </div>
          ) : (
            <div className={`grid gap-2 ${total <= 2 ? 'grid-cols-2' : total <= 4 ? 'grid-cols-2 md:grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
              {Array.from({ length: total }).map((_, i) => {
                const slide = slides[i];
                return (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden" style={{ background: T.hover }}>
                    {slide?.url ? (
                      <img
                        src={slide.url}
                        alt={slide.caption}
                        className="w-full h-full object-cover cursor-zoom-in transition-opacity hover:opacity-90"
                        onClick={() => setLightbox(slide)}
                        data-testid={`llm-studio-image-slide-${i + 1}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: T.border, borderTopColor: T.accentFg }} />
                      </div>
                    )}
                    <div
                      className="absolute bottom-1 left-1 text-[11px] font-mono px-1.5 py-0.5 rounded"
                      style={{ background: 'rgba(0,0,0,0.6)', color: T.primary }}
                    >{slide?.caption || `Slide ${String(i + 1).padStart(2, '0')}`}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {lightbox && (
        <Lightbox url={lightbox.url} alt={lightbox.caption} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

// ═══════════════════ IMAGE-GEN MODAL ═══════════════════
function ImageGenModal({ prompt, setPrompt, count, setCount, size, setSize, onCancel, onSubmit }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6"
        style={{ background: T.bg, border: `1px solid ${T.border}`, boxShadow: '0 12px 40px rgba(0,0,0,0.12)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="llm-studio-image-modal"
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5" style={{ color: T.accentFg }} />
          <h3 className="text-[17px] font-semibold" style={{ color: T.primary }}>Generate Images</h3>
        </div>
        <label className="text-[12px] font-semibold uppercase tracking-wide block mb-2" style={{ color: T.secondary }}>Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="e.g. Cinematic dusty pitch, golden hour lighting, 4K"
          rows={3}
          className="w-full rounded-lg p-3 text-[14px] focus:outline-none"
          style={{ background: T.hover, border: `1px solid ${T.border}`, color: T.primary }}
          data-testid="llm-studio-image-prompt"
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-wide block mb-2" style={{ color: T.secondary }}>Count</label>
            <select
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-full rounded-lg p-2 text-[14px] focus:outline-none"
              style={{ background: T.hover, border: `1px solid ${T.border}`, color: T.primary }}
              data-testid="llm-studio-image-count"
            >
              {[1, 2, 3, 4, 6, 9].map(n => <option key={n} value={n}>{n} slides</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-semibold uppercase tracking-wide block mb-2" style={{ color: T.secondary }}>Aspect</label>
            <select
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="w-full rounded-lg p-2 text-[14px] focus:outline-none"
              style={{ background: T.hover, border: `1px solid ${T.border}`, color: T.primary }}
              data-testid="llm-studio-image-size"
            >
              <option value="1024x1024">1:1 (1024×1024)</option>
              <option value="1536x1024">16:9 (1536×1024)</option>
              <option value="1024x1536">9:16 (1024×1536)</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-[14px]"
            style={{ color: T.secondary }}
            onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >Cancel</button>
          <button
            onClick={onSubmit}
            disabled={!prompt.trim()}
            className="px-4 py-2 rounded-lg text-[14px] font-semibold"
            style={{
              background: prompt.trim() ? T.sendActive : T.sendIdle,
              color: prompt.trim() ? '#FFFFFF' : T.tertiary,
              cursor: prompt.trim() ? 'pointer' : 'not-allowed',
              opacity: prompt.trim() ? 1 : 0.5,
            }}
            data-testid="llm-studio-image-submit"
          >Generate</button>
        </div>
      </div>
    </div>
  );
}
